import { useMemo } from 'react'
import { useMenuEngineering } from '../hooks/useMenuEngineering.js'
import { formatMoney } from '../lib/format.js'

// Top 5 margin contributors over the last 90 days. Sits between the
// menu engineering matrix and the kill-candidates card so the manager
// can see "where the money actually comes from" right after the
// strategic view.
//
// Reads the same hook as MenuEngineeringCard (no duplicate query) and
// renders nothing if there's no data — the empty state is already
// handled by the card above.

// Tone for money columns. Negative values render red so a margin
// contributor that's actually losing money over 90 days is obvious
// at a glance. Strict `<` keeps -0 and 0 on the emerald (positive)
// branch, and any non-finite value (NaN, null coerced via Number)
// also falls through to emerald — matches the prior default.
// WCAG-AA: text-red-700 on white = 5.74:1.
function moneyTone(n) {
  return Number(n) < 0 ? 'text-red-700' : 'text-emerald-800'
}

export default function MenuMarginLeaderboardCard() {
  const { rows, hasData, loading, error } = useMenuEngineering()

  const top = useMemo(() => {
    return [...(rows || [])]
      .sort((a, b) => Number(b.cm_total_90d || 0) - Number(a.cm_total_90d || 0))
      .slice(0, 5)
  }, [rows])

  if (loading || error || !hasData || top.length === 0) return null

  return (
    <section className="mx-4 mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
        Top margin contributors · 90d
      </div>
      <p className="mt-1 text-xs text-black/60 leading-snug">
        Where your gross profit actually comes from. Protect these items.
      </p>

      <ul className="mt-3 divide-y divide-black/5 rounded-xl bg-black/[0.02]">
        {top.map((r, i) => (
          <li
            key={r.item_id}
            className="flex items-baseline justify-between gap-3 px-3 py-2.5"
          >
            <div className="flex min-w-0 items-baseline gap-2">
              <div className="w-4 shrink-0 text-xs font-bold tabular-nums text-black/45">
                {i + 1}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-black/90">
                  {r.item_name}
                </div>
                <div className="mt-0.5 text-[11px] text-black/55 tabular-nums">
                  {Math.round(Number(r.units_90d || 0))}u ·{' '}
                  <span className={moneyTone(r.cm_per_unit)}>
                    {formatMoney(Number(r.cm_per_unit || 0))}
                  </span>{' '}
                  per
                </div>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className={`text-sm font-bold tabular-nums ${moneyTone(r.cm_total_90d)}`}>
                {formatMoney(Number(r.cm_total_90d || 0))}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-black/45">
                90d profit
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
